"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Link from "next/link";

import MultiSelect from "@/components/form/multi-select";
import Select from "@/components/form/select";
import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";

import VariableConcordanceTable from "./variable-concordance-table";

import api from "@/lib/api";

type VacHomeProps = {
  topic: { value: number; label: string }[];
  subtopic: { value: number; label: string }[];
  variable: { value: number; label: string }[];
};

type VACDataProps = {
  info: {
    topic: string;
    subtopic: string;
    subgroup_messages: { [key: string]: string };
  };
  table: {
    variable_name: string;
    subgroup_name: string;
    variable_description: string;
    datasets: {
      dataset_title: string;
      association_text: string;
      associatoin_note_index: number | null;
    }[];
  }[];
  notes: { [key: string]: string };
};

type FormValues = {
  topic?: number;
  subtopic?: number;
  variable?: number[];
};

export async function getVACdata(variables: Array<number>) {
  const queryParams = new URLSearchParams({});
  if (variables?.length) queryParams.append("variables", variables.join(","));
  const response = await api.get<VACDataProps>(`/vac/data/hcap/?${queryParams.toString()}`);
  return response;
}
export default function VariableConcordanceHome() {
  const { control, watch, reset } = useForm<FormValues>({
    defaultValues: {
      topic: undefined,
      subtopic: undefined,
      variable: [],
    },
  });

  const topicValues = watch("topic");
  const subtopicValues = watch("subtopic");
  const variableValues = watch("variable");

  const [topics, setTopics] = useState<{ value: number; label: string }[]>([]);
  const [subtopics, setSubtopics] = useState<{ value: number; label: string }[]>([]);
  const [variables, setVariables] = useState<{ value: number; label: string }[]>([]);
  const [tableData, setTableData] = useState<VACDataProps>();
  const [loading, setIsLoading] = useState<boolean>(false);

  const { isPending, error, data } = useQuery<VacHomeProps, Error>({
    queryKey: ["vac-home-hcap"],
    queryFn: async () => await api.get(`/vac/home/hcap`),
  });

  useEffect(() => {
    if (data) {
      setTopics(data.topic || []);
      setSubtopics(data.subtopic || []);
      setVariables(data.variable || []);

      const firstTopic = data.topic?.[0]?.value ?? undefined;

      reset((prevValues) => ({
        ...prevValues,
        topic: firstTopic,
        subtopic: data.subtopic?.[0]?.value ?? undefined,
        variable: data.variable?.[0] ? [data.variable[0].value] : [],
      }));
    }
  }, [data, reset]);

  useEffect(() => {
    const fetchSubtopics = async () => {
      if (topicValues !== undefined) {
        try {
          const response = await api.get<
            {
              value: number;
              label: string;
            }[]
          >(`/vac/subtopic-dropdown/${topicValues}`);

          const subtopics = response || [];
          setSubtopics(subtopics);

          reset((prevValues) => ({
            ...prevValues,
            subtopic: subtopics[0]?.value ?? "",
          }));
        } catch (err) {
          console.error("Error fetching topics:", err);
        }
      }
    };
    fetchSubtopics();
  }, [topicValues, reset]);

  useEffect(() => {
    const fetchVariables = async () => {
      if (subtopicValues !== undefined) {
        try {
          const response = await api.get<
            {
              value: number;
              label: string;
            }[]
          >(`/vac/variable-dropdown/${subtopicValues}`);

          const variables = response || [];
          setVariables(variables);
          reset((prevValues) => ({
            ...prevValues,
            variable: variables.map((item) => item.value),
          }));
        } catch (err) {
          console.error("Error fetching measures:", err);
        }
      }
    };
    fetchVariables();
  }, [subtopicValues, reset]);

  useEffect(() => {
    const fetchData = async () => {
      if (variableValues && variableValues.length > 0) {
        setIsLoading(true);
        const response = await getVACdata(variableValues);
        setTableData(response);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [variableValues]);

  const resetForm = () => {
    setIsLoading(true);
    reset({
      topic: data?.topic?.[0]?.value ?? undefined,
      subtopic: data?.subtopic?.[0]?.value ?? undefined,
      variable: data?.variable?.[0]?.value ? [data.variable[0].value] : [],
    });
    setTableData(undefined);
    setIsLoading(false);
  };

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  if (topicValues == undefined && subtopicValues == undefined && variableValues?.length == 0)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 w-100">
        <Loading />
      </div>
    );

  return (
    <>
      <form>
        <div className="text-end">
          <Link className="g2-link-blue" href="/hcap-studies/question-concordancee">
            Go to Question Concordance
          </Link>
        </div>
        <div className="row mt-2">
          <div className="col-md-6 col-sm-12 mt-2">
            <label className="form-label text-uppercase fw-semibold">Topic : Subtopic</label>
            <div className="input-group d-flex w-100">
              <div style={{ flex: 1 }}>
                <Select
                  options={topics}
                  control={control}
                  isPending={isPending}
                  name="topic"
                  label=""
                  defaultValue={0}
                  isClearable={false}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Select
                  options={subtopics}
                  control={control}
                  isPending={isPending}
                  name="subtopic"
                  label=""
                  defaultValue={0}
                  isClearable={false}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6 col-sm-12 mt-2">
            <MultiSelect
              options={variables || []}
              control={control}
              isPending={isPending}
              name="variable"
              label="Variable Name"
            />
          </div>
        </div>
        <div className="row my-4">
          <div className="text-end d-block">
            <button
              type="button"
              className="btn btn-outline-secondary"
              id="reset"
              title="Reset"
              onClick={() => resetForm()}
            >
              <i className="bi bi-arrow-repeat"></i> Reset
            </button>
          </div>
        </div>

        {variableValues && variableValues.length === 0 && data && data.variable?.length !== 0 && (
          <div id="remote-data" className="mt-4">
            <div className="alert alert-secondary" role="alert">
              Please select at least one variable.
            </div>
          </div>
        )}
      </form>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center vh-100 w-100">
          <Loading />
        </div>
      ) : (
        tableData != undefined && variableValues?.length != 0 && <VariableConcordanceTable data={tableData} />
      )}
    </>
  );
}
